import { useEffect, useMemo, useState, useRef } from "react";
import cx from "classnames";
import useClickAway from "../../../common/useClickAway.js";
import useHandleWindowResize from "../../../common/useHandleWindowResize.js";
import useDetectUserAgent from "../../../common/useDetectUserAgent.js";
import AuxiliaryNav from "../AuxiliaryNav/AuxiliaryNav.jsx";
import MenuItem from "../PrimaryNav/MenuItem.jsx";
import MobileMenuItemDropdown from "./MobileMenuItemDropdown.jsx";
import Icon from "../../Icon/Icon.jsx";

import uniqueId from "lodash.uniqueid";
import FocusLock from "react-focus-lock";
import { object, array, bool } from "prop-types";
import { flyoutNavShape } from "../PrimaryNav/primaryNavTypes.js";

const propTypes = {
  dataExplorer: object,
  navItems: flyoutNavShape,
  search: object,
  draftReport: array,
  siteAlertSet: bool,
};

const MobileMenu = (props) => {
  const { dataExplorer, navItems, search, draftReport, siteAlertSet } = props;

  // See \frontend\EGH\scss\_breakpoints.scss
  const breakpointLarge = 910; // $bp-large
  const breakpointLargeMax = breakpointLarge - 1; // $bp-x-large-max

  const [isOpen, setOpen] = useState(false);
  const [childMenuOpen, setChildMenuOpen] = useState("");
  const [menuItemDimensions, setMenuItemDimensions] = useState({});

  const menuListRef = useRef();

  const navRef = useClickAway({ isOpen, setOpen });
  const { isDesktop } = useHandleWindowResize({
    breakpoint: breakpointLargeMax,
  });
  const windowsPlatformClassname = useDetectUserAgent();

  const handleForMenuToggle = (open) => {
    setOpen(open);
  };

  useEffect(() => {
    if (isDesktop) {
      setOpen(false);
      setChildMenuOpen("");
    }
  }, [isDesktop]);

  useEffect(() => {
    document.querySelector("body").classList.toggle("mobile-menu-open", isOpen);
    const loginButton = document.querySelector(".global-header__login");
    if (!isOpen) {
      setChildMenuOpen("");
      loginButton.style.display = "block";
    } else {
      loginButton.style.display = "none";
    }
  }, [isOpen]);

  function handleResize() {
    const alert = document.querySelector(".sitewide-alert");
    const headerContainer = document.querySelector(".global-header__container");
    if (alert && headerContainer) {
      const alertDimensions = alert.getBoundingClientRect();
      const headerDimentions = headerContainer.getBoundingClientRect();
      setMenuItemDimensions({
        height: Math.round(alertDimensions.height + headerDimentions.height),
      });
    }
  }
  useEffect(() => {
    handleResize();
  }, [siteAlertSet]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const menuId = useMemo(() => uniqueId("mobile-menu-"), []);

  return (
    <div className="global-header__primary-nav--mobile">
      <FocusLock returnFocus={true} disabled={isDesktop || !isOpen}>
        <nav
          ref={navRef}
          className={cx(`mobile-menu primary-nav ${windowsPlatformClassname}`, {
            "mobile-menu--is-open": isOpen,
          })}
          aria-label="Primary Navigation"
        >
          <div className="mobile-menu__auxillery-nav">
            <AuxiliaryNav
              draftReport={draftReport}
              search={search}
              dataExplorer={dataExplorer}
            />
          </div>
          <button
            type="button"
            className="mobile-menu__button"
            onClick={() => handleForMenuToggle((open) => !open)}
            aria-expanded={isOpen}
            aria-controls={menuId}
          >
            {isOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M20.1782 19.631C20.2539 19.7083 20.3207 19.7947 20.3675 19.8913C20.4811 20.0845 20.5379 20.2869 20.5379 20.4994C20.5379 20.712 20.4811 20.9155 20.3764 21.0996C20.3285 21.1951 20.2628 21.2826 20.1882 21.3701L14.4127 27.2536C14.0908 27.5821 13.5708 27.5821 13.2489 27.2536C12.917 26.9251 12.917 26.3932 13.2489 26.0646L18.6831 20.5L13.2489 14.9342C12.917 14.6057 12.917 14.0749 13.2489 13.7464C13.5708 13.4179 14.0908 13.4179 14.4127 13.7464L20.1782 19.631Z"
                  fill="#00416B"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M19.8218 21.369C19.7461 21.2917 19.6793 21.2053 19.6325 21.1087C19.5189 20.9155 19.4621 20.7131 19.4621 20.5006C19.4621 20.288 19.5189 20.0845 19.6236 19.9004C19.6715 19.8049 19.7372 19.7174 19.8118 19.6299L25.5873 13.7464C25.9092 13.4179 26.4292 13.4179 26.7511 13.7464C27.083 14.0749 27.083 14.6068 26.7511 14.9354L21.3169 20.5L26.7511 26.0658C27.083 26.3943 27.083 26.9251 26.7511 27.2536C26.4292 27.5821 25.9092 27.5821 25.5873 27.2536L19.8218 21.369Z"
                  fill="#00416B"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="40"
                height="40"
                viewBox="0 0 40 40"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M11 14H29"
                  stroke="#0C4456"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M11 26H29"
                  stroke="#0C4456"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M11 20H29"
                  stroke="#0C4456"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>

          <div
            className="mobile-menu__menu"
            id={menuId}
            style={{
              height: `calc(100vh - ${menuItemDimensions.height}px)`,
              top: `${menuItemDimensions.height + 1}px`,
            }}
          >
            <ul className="mobile-menu__items" ref={menuListRef}>
              {navItems.map((item, i) => {
                if (item.children && item.children.length > 0) {
                  return (
                    <MobileMenuItemDropdown
                      item={item}
                      key={i}
                      childMenuOpen={childMenuOpen}
                      setChildMenuOpen={setChildMenuOpen}
                      menuItemDimensions={menuItemDimensions}
                    />
                  );
                } else {
                  return (
                    <li
                      key={i}
                      className={cx("mobile-menu__item", {
                        "mobile-menu__item--open": childMenuOpen,
                      })}
                    >
                      <a
                        className="primary-nav__item--mobile"
                        href={item.href}
                        target={item.target}
                      >
                        {item.title}
                      </a>
                    </li>
                  );
                }
              })}
            </ul>
          </div>
        </nav>
      </FocusLock>
    </div>
  );
};

MobileMenu.propTypes = propTypes;
export default MobileMenu;
